// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TitleTypography, { TitleTypographyStyles } from "@west/App/Components/TitleTypography/TitleTypography";
import { xGoogleAnalyticsUpdate } from "@west/App/Handlers/GoogleAnalytics/xGoogleAnalytics";
import { CustomAppState, xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    // XNG-Changes
    showTitle = "showTitle",
    showViewAll = "showViewAll",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.showTitle]: boolean;
        [fields.showViewAll]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;

    /**
     * XNG-Changes: West-539
     * we need it when we have multiple recentOrders widget with different numberOfRecords
     * without this code, the second widget will take recentOrdersParameter default value and see the value is existing and doesn't call loadOrders
     * then when update numberOfRecords in didMount, will not re-call LoadOrders
     */
    recentOrdersParameter.pageSize = ownProps.fields.numberOfRecords;

    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Change
        tabs: (state as CustomAppState).xTabs,
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        isHomepage: getCurrentPage(state).type === "HomePage",
    };
};

const mapDispatchToProps = {
    loadOrders,
    updateZone: (zone: string, enabled: boolean) => ({
        type: "xTabs/EnableZone",
        data: {
            zone,
            isEnabled: enabled, // we need to show this tab if we are in tab context
        },
    }),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    viewAllGridItem?: GridItemProps;
    viewAllLink?: LinkPresentationProps;
    titleTypographyStyles?: TitleTypographyStyles;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
};

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "orderDate DESC,erpOrderNumber DESC,webOrderNumber DESC",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    // XNG-change
    componentDidUpdate(): void {
        if (this.props.canViewOrders && this.props.ordersDataView.value && !this.props.tabs[this.props.zone]) {
            this.props.updateZone(this.props.zone, true);
        } else if (!this.props.canViewOrders || !this.props.ordersDataView.value) {
            this.props.updateZone(this.props.zone, false);
        }
    }

    render() {
        const {
            ordersDataView,
            orderHistoryPageLink,
            settingsCollection,
            canViewOrders,
            fields: { showTitle, showViewAll },
            xGoogleAnalytics,
            isHomepage,
        } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        const gaRecentOrdersClickHandler = () => {
            if (xGoogleAnalytics.hpRecentOrdersEvent && isHomepage) {
                const dataLayer = getDataLayer();
                if (!dataLayer) {
                    return;
                }

                const googleAnalyticsData = {
                    event: xGoogleAnalytics.hpRecentOrdersEventName,
                    click_text: translate("View All Orders"),
                    destination: orderHistoryUrl,
                    affiliation: xGoogleAnalytics.affiliation,
                    authentication_state: xGoogleAnalytics.authenticationState,
                };

                dataLayer.push(googleAnalyticsData);
            }
        };

        return (
            <StyledWrapper {...customCssWrapper}>
                <CardList extendedStyles={styles.cardList} data-test-selector="widget_RecentOrders">
                    {/* XNG-Changes */}
                    {showTitle && (
                        <TitleTypography
                            linkText={translate("View All Recent Orders")}
                            link={orderHistoryUrl}
                            extendedStyles={styles.titleTypographyStyles}
                        >
                            {translate("Recent Orders")}
                        </TitleTypography>
                    )}
                    {ordersDataView.value.length === 0 ? (
                        <GridItem className="no-orders-found-grid-item" {...styles.noOrdersFoundGridItem}>
                            <Typography
                                className="no-orders-found-text"
                                {...styles.noOrdersFoundText}
                                data-test-selector="recentOrders_NoOrdersFound"
                            >
                                {translate("No orders found")}
                            </Typography>
                        </GridItem>
                    ) : (
                        <>
                            <GridItem width={12}>
                                <DataTable {...styles.dataTable}>
                                    <DataTableHead {...styles.dataTableHead}>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("Date")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("Order Number")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("Status (TBD)")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeaders} alignX="right">
                                            {translate("Total")}
                                        </DataTableHeader>
                                        <DataTableHeader {...styles.dataTableHeaders}>
                                            {translate("Tracking Number")}
                                        </DataTableHeader>
                                    </DataTableHead>
                                    <DataTableBody {...styles.dataTableBody}>
                                        {ordersDataView.value.map(
                                            order =>
                                                order !== null && (
                                                    <OrderSummaryCard
                                                        key={order.id}
                                                        order={order}
                                                        orderSettings={settingsCollection.orderSettings}
                                                        extendedStyles={styles.orderSummaryCard}
                                                    />
                                                ),
                                        )}
                                    </DataTableBody>
                                </DataTable>
                            </GridItem>
                            {showViewAll && (
                                <GridItem {...styles.viewAllGridItem}>
                                    <Link
                                        href={orderHistoryUrl}
                                        {...styles.viewAllLink}
                                        onClick={gaRecentOrdersClickHandler}
                                    >
                                        {translate("View All Orders")}
                                    </Link>
                                </GridItem>
                            )}
                        </>
                    )}
                </CardList>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.showTitle,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.showViewAll,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
